import React, { useEffect, useMemo, useState } from 'react';
import cccApi, { RecuperacaoMarcacao, RetencaoMarcacao } from '../api/cccApi';
import { isValidMarcacaoId } from '../config/prospeccaoMarcacao';
import ComercialManagement from '@crm/components/Comercial/ComercialManagement';
import '@crm/components/Comercial/ComercialManagement.css';

type MarcacaoSeed = {
  id?: number;
  marcacao_id?: string;
  nome_empresa?: string;
  nome_responsavel?: string;
  morada?: string;
  contacto_cliente?: string;
  form_data?: Record<string, unknown>;
  [key: string]: unknown;
};

type Props = {
  marcacao: RecuperacaoMarcacao | RetencaoMarcacao;
  mode?: 'solicitar' | 'refazer';
  /** Which CCC workspace owns this lead — controls submit API + BO source tag. */
  workspace?: 'recuperacao' | 'retencao';
  onClose: () => void;
  onSuccess: () => void;
};

const swallowOverlayBackdropPointer = (e: React.MouseEvent) => {
  if (e.target === e.currentTarget) {
    e.stopPropagation();
  }
};

const CccSolicitarPropostaOverlay: React.FC<Props> = ({
  marcacao,
  mode = 'solicitar',
  workspace = 'recuperacao',
  onClose,
  onSuccess,
}) => {
  const [seed, setSeed] = useState<Record<string, MarcacaoSeed> | null>(null);
  const marcacaoId = useMemo(() => {
    const raw = marcacao.marcacao_id || (marcacao.id != null ? String(marcacao.id) : '');
    return isValidMarcacaoId(raw) ? raw.trim() : '';
  }, [marcacao]);

  useEffect(() => {
    if (!marcacaoId) return;
    let cancelled = false;

    const fallbackSeed: Record<string, MarcacaoSeed> = {
      [marcacaoId]: {
        ...marcacao,
        marcacao_id: marcacaoId,
        empresa: marcacao.nome_empresa,
        responsavel: marcacao.nome_responsavel,
        contacto: marcacao.contacto_cliente,
        form_data: marcacao.form_data,
      } as MarcacaoSeed,
    };

    const normalizeSeedEntry = (raw: MarcacaoSeed): MarcacaoSeed => ({
      ...raw,
      marcacao_id: raw.marcacao_id || marcacaoId,
      empresa: (raw as { empresa?: string }).empresa || raw.nome_empresa || marcacao.nome_empresa,
      responsavel:
        (raw as { responsavel?: string }).responsavel ||
        raw.nome_responsavel ||
        marcacao.nome_responsavel,
      contacto:
        (raw as { contacto?: string }).contacto ||
        raw.contacto_cliente ||
        marcacao.contacto_cliente,
      form_data:
        raw.form_data && typeof raw.form_data === 'object'
          ? raw.form_data
          : marcacao.form_data,
    });

    (async () => {
      try {
        const data =
          workspace === 'retencao'
            ? await cccApi.getRetencaoMarcacao(marcacaoId)
            : await cccApi.getRecuperacaoMarcacao(marcacaoId);
        if (!cancelled && data?.success && data.marcacao) {
          setSeed({
            [marcacaoId]: normalizeSeedEntry(data.marcacao as MarcacaoSeed),
          });
          return;
        }
      } catch {
        /* fallback to board payload */
      }
      if (!cancelled) {
        setSeed(fallbackSeed);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [marcacao, marcacaoId, workspace]);

  if (!marcacaoId) {
    return (
      <div className="rec-solicitar-proposta-overlay" role="presentation">
        <div className="rec-solicitar-proposta-loading">
          <p>Marcação inválida — não foi possível abrir Solicitar Proposta.</p>
          <button type="button" className="rec-btn rec-btn-ghost" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    );
  }

  if (!seed) {
    return (
      <div className="rec-solicitar-proposta-overlay" role="presentation">
        <div className="rec-solicitar-proposta-loading">A carregar formulário…</div>
      </div>
    );
  }

  return (
    <div
      className="rec-solicitar-proposta-overlay"
      role="presentation"
      onMouseDown={swallowOverlayBackdropPointer}
    >
      <ComercialManagement
        embeddedProposalOnly
        embeddedProposalMode={mode}
        embeddedProposalMarcacaoId={marcacaoId}
        embeddedCccWorkspace={workspace}
        embeddedMarcacaoSeed={seed as Record<string, never>}
        onEmbeddedProposalClose={onClose}
        onEmbeddedProposalSuccess={onSuccess}
      />
    </div>
  );
};

export default CccSolicitarPropostaOverlay;
